import React, { createContext, useContext, useReducer, useEffect, ReactNode } from 'react';
import { cartAPI } from '@services/api';
import { STORAGE_KEYS, PRICING } from '@utils/constants';
import { getStorageItem, setStorageItem, generateId } from '@utils/helpers';
import type { CartItem, CartContextType } from '@/types';

interface CartState {
  items: CartItem[];
  discountCode: string | null;
  discountAmount: number;
  loading: boolean;
  error: string | null;
}

type CartAction =
  | { type: 'ADD_ITEM'; payload: Omit<CartItem, 'id' | 'addedAt'> }
  | { type: 'REMOVE_ITEM'; payload: string }
  | { type: 'UPDATE_ITEM'; payload: { id: string; updates: Partial<CartItem> } }
  | { type: 'CLEAR_CART' }
  | { type: 'APPLY_DISCOUNT'; payload: { code: string; amount: number } }
  | { type: 'REMOVE_DISCOUNT' }
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_ERROR'; payload: string | null }
  | { type: 'LOAD_CART'; payload: CartState };

const initialState: CartState = {
  items: [],
  discountCode: null,
  discountAmount: 0,
  loading: false,
  error: null,
};

const cartReducer = (state: CartState, action: CartAction): CartState => {
  switch (action.type) {
    case 'ADD_ITEM': {
      const existingItemIndex = state.items.findIndex(
        item =>
          item.photoId === action.payload.photoId && item.resolution === action.payload.resolution
      );

      if (existingItemIndex !== -1) {
        const updatedItems = [...state.items];
        updatedItems[existingItemIndex] = {
          ...updatedItems[existingItemIndex],
          quantity: updatedItems[existingItemIndex].quantity + action.payload.quantity,
        };

        return {
          ...state,
          items: updatedItems,
        };
      }

      const newItem: CartItem = {
        ...action.payload,
        id: generateId(),
        addedAt: new Date().toISOString(),
      };

      return {
        ...state,
        items: [...state.items, newItem],
      };
    }

    case 'REMOVE_ITEM':
      return {
        ...state,
        items: state.items.filter(item => item.id !== action.payload),
      };

    case 'UPDATE_ITEM':
      return {
        ...state,
        items: state.items.map(item =>
          item.id === action.payload.id ? { ...item, ...action.payload.updates } : item
        ),
      };

    case 'CLEAR_CART':
      return {
        ...state,
        items: [],
        discountCode: null,
        discountAmount: 0,
      };

    case 'APPLY_DISCOUNT':
      return {
        ...state,
        discountCode: action.payload.code,
        discountAmount: action.payload.amount,
        error: null,
      };

    case 'REMOVE_DISCOUNT':
      return {
        ...state,
        discountCode: null,
        discountAmount: 0,
      };

    case 'SET_LOADING':
      return {
        ...state,
        loading: action.payload,
      };

    case 'SET_ERROR':
      return {
        ...state,
        error: action.payload,
      };

    case 'LOAD_CART':
      return {
        ...state,
        ...action.payload,
      };

    default:
      return state;
  }
};

const CartContext = createContext<CartContextType | null>(null);

interface CartProviderProps {
  children: ReactNode;
}

export const CartProvider: React.FC<CartProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(cartReducer, initialState);

  // Load cart from localStorage on mount
  useEffect(() => {
    const savedCart = getStorageItem(STORAGE_KEYS.CART_DATA, null);
    if (savedCart) {
      dispatch({ type: 'LOAD_CART', payload: savedCart });
    }
  }, []);

  // Save cart to localStorage whenever it changes
  useEffect(() => {
    if (state.items.length > 0 || state.discountCode) {
      setStorageItem(STORAGE_KEYS.CART_DATA, {
        items: state.items,
        discountCode: state.discountCode,
        discountAmount: state.discountAmount,
      });
    } else {
      setStorageItem(STORAGE_KEYS.CART_DATA, null);
    }
  }, [state.items, state.discountCode, state.discountAmount]);

  // Calculated values
  const itemCount = state.items.reduce((total, item) => total + item.quantity, 0);

  const subtotal = state.items.reduce((total, item) => total + item.price * item.quantity, 0);

  // Apply package discounts
  const packageDiscount = (() => {
    if (itemCount >= PRICING.packages.large.threshold) {
      return subtotal * PRICING.packages.large.discount;
    } else if (itemCount >= PRICING.packages.medium.threshold) {
      return subtotal * PRICING.packages.medium.discount;
    } else if (itemCount >= PRICING.packages.small.threshold) {
      return subtotal * PRICING.packages.small.discount;
    }
    return 0;
  })();

  const totalDiscountAmount = packageDiscount + state.discountAmount;
  const total = Math.max(0, subtotal - totalDiscountAmount);

  // Cart methods
  const addToCart = (item: Omit<CartItem, 'id' | 'addedAt'>): void => {
    dispatch({ type: 'ADD_ITEM', payload: item });
  };

  const removeFromCart = (itemId: string): void => {
    dispatch({ type: 'REMOVE_ITEM', payload: itemId });
  };

  const updateItem = (itemId: string, updates: Partial<CartItem>): void => {
    dispatch({
      type: 'UPDATE_ITEM',
      payload: { id: itemId, updates },
    });
  };

  const clearCart = (): void => {
    dispatch({ type: 'CLEAR_CART' });
  };

  const isInCart = (photoId: string, resolution: string = 'web'): boolean => {
    return state.items.some(item => item.photoId === photoId && item.resolution === resolution);
  };

  const getItemQuantity = (photoId: string, resolution: string = 'web'): number => {
    const item = state.items.find(
      item => item.photoId === photoId && item.resolution === resolution
    );
    return item ? item.quantity : 0;
  };

  const applyDiscount = async (code: string): Promise<boolean> => {
    dispatch({ type: 'SET_LOADING', payload: true });
    dispatch({ type: 'SET_ERROR', payload: null });

    try {
      const response = await cartAPI.validateDiscount(code, subtotal);

      if (response.data.valid) {
        dispatch({
          type: 'APPLY_DISCOUNT',
          payload: {
            code,
            amount: response.data.discount,
          },
        });
        return true;
      } else {
        dispatch({
          type: 'SET_ERROR',
          payload: 'Invalid discount code',
        });
        return false;
      }
    } catch (error: any) {
      dispatch({
        type: 'SET_ERROR',
        payload: error.message || 'Failed to apply discount code',
      });
      return false;
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  };

  const removeDiscount = (): void => {
    dispatch({ type: 'REMOVE_DISCOUNT' });
  };

  const value: CartContextType = {
    items: state.items,
    itemCount,
    subtotal,
    total,
    discountAmount: totalDiscountAmount,
    discountCode: state.discountCode,
    loading: state.loading,
    addToCart,
    removeFromCart,
    updateItem,
    clearCart,
    isInCart,
    getItemQuantity,
    applyDiscount,
    removeDiscount,
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};

export const useCart = (): CartContextType => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within CartProvider');
  }
  return context;
};
